/**
 * AIImagePromptEditor Component
 *
 * AI-assisted image prompt editing interface with instruction input
 */

import { memo, useState, useCallback } from 'react';
import { useAIPromptEdit } from '@/hooks/useAIPromptEdit';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { ErrorMessage } from '@/components/ui/ErrorMessage';
import { Sparkles, X, Loader2 } from 'lucide-react';

interface IAIImagePromptEditorProps {
  promptId: string;
  postId: string;
  originalPrompt: string;
  onSaveSuccess: (editedPrompt: string) => void;
  onCancel: () => void;
}

const AIImagePromptEditor = ({
  promptId,
  postId,
  originalPrompt,
  onSaveSuccess,
  onCancel,
}: IAIImagePromptEditorProps) => {
  // ============================================
  // 1. HOOKS
  // ============================================
  const [instruction, setInstruction] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const { isEditing, isSaving, error, executeEdit, cancelEdit, saveEdit, reset } = useAIPromptEdit({
    promptId,
    postId,
    originalPrompt,
    onSuccess: async prompt => {
      setLocalError(null);
      // Auto-save after AI editing completes
      const success = await saveEdit(prompt);
      if (success) {
        onSaveSuccess(prompt);
      }
    },
    onError: err => {
      setLocalError(err.message);
    },
  });

  // ============================================
  // 2. HELPER FUNCTIONS
  // ============================================
  const handleInstructionChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInstruction(e.target.value);
    setLocalError(null);
  }, []);

  const handleExecuteEdit = useCallback(() => {
    if (!instruction.trim()) {
      setLocalError('Please provide editing instructions');
      return;
    }

    executeEdit(instruction);
  }, [instruction, executeEdit]);

  const handleCancel = useCallback(() => {
    if (isEditing) {
      cancelEdit();
    }
    reset();
    setInstruction('');
    setLocalError(null);
    onCancel();
  }, [isEditing, cancelEdit, reset, onCancel]);

  // ============================================
  // 3. COMPUTED VALUES
  // ============================================
  const displayError = error?.message || localError;
  const canExecute = !isEditing && !isSaving && instruction.trim().length > 0;
  const isProcessing = isEditing || isSaving;

  // ============================================
  // 4. SINGLE RETURN
  // ============================================
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold">AI-Assisted Prompt Editing</h3>
      </div>

      {/* Original Prompt Display */}
      <div className="space-y-2">
        <Label>Original Prompt</Label>
        <div className="rounded-md border border-border bg-muted/30 p-3">
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-muted-foreground">
            {originalPrompt}
          </p>
        </div>
        <div className="text-xs text-muted-foreground text-right">
          {originalPrompt.length} characters
        </div>
      </div>

      {/* Instruction Input */}
      <div className="space-y-2">
        <Label htmlFor="ai-prompt-instruction">
          Editing Instructions
          <span className="text-sm text-muted-foreground ml-2">
            (e.g., "Add sunset lighting", "Change to watercolor style")
          </span>
        </Label>
        <Textarea
          id="ai-prompt-instruction"
          value={instruction}
          onChange={handleInstructionChange}
          placeholder="Describe how you want to modify the image prompt..."
          className="min-h-[80px]"
          disabled={isEditing || isSaving}
          maxLength={300}
        />
        <div className="flex gap-2">
          <Button
            onClick={handleExecuteEdit}
            disabled={!canExecute}
            variant="default"
            size="sm"
            className="flex-1 sm:flex-none"
          >
            {isEditing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Editing...
              </>
            ) : (
              <>
                <Sparkles className="mr-2 h-4 w-4" />
                Apply AI Edit
              </>
            )}
          </Button>
          <Button
            onClick={handleCancel}
            variant="outline"
            size="sm"
            disabled={isEditing || isSaving}
          >
            <X className="mr-2 h-4 w-4" />
            Cancel
          </Button>
        </div>
      </div>

      {/* Error Display */}
      {displayError && <ErrorMessage message={displayError} />}

      {/* Processing Status */}
      {isProcessing && (
        <div className="flex items-center justify-center gap-2 p-4 rounded-md bg-muted/30">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
          <span className="text-sm font-medium">
            {isEditing && 'AI is editing your prompt...'}
            {isSaving && 'Saving and regenerating image...'}
          </span>
        </div>
      )}
    </div>
  );
};

export const AIImagePromptEditorMemo = memo(AIImagePromptEditor);
